import React, { useContext, useEffect, useState, useRef } from "react";
import { Image, Table, Tag, Row, Col } from "antd";
import { useNavigate } from "react-router-dom";
import { getTablePagination } from "../../../utils/tablePagination";
import { getPatientListApi } from "../../../services/receptionist.services";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import bedIcon from "../../../assets/images/patientIcons/bed.svg";
import { UiDataTable } from "../../ui";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import useIsMobile from "../../../hooks/useIsMobile";
import usePageFillTableScroll from "../../../hooks/usePageFillTableScroll";

const PatientsTable = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const isMobile = useIsMobile();
  const tableWrapRef = useRef(null);
  const [patientData, setPatientData] = useState([]);
  const [page, setPage] = useState(() => parsePageParam(params));
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => parseSearchParam(params));
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const tableScroll = usePageFillTableScroll(
    tableWrapRef,
    isMobile,
    "default",
    patientData?.data?.length ?? 0
  );

  useEffect(() => {
    setLoading(true);
    let queryParams = {
      page: page,
      limit: 50,
    };
    if (searchQuery) {
      queryParams.search = searchQuery;
      queryParams.page = 1;
    }
    if (defaultRole === "Facility_Admin") {
      queryParams.facility_id =
        Number(getCookie(FacilityID)) || userDetails?.currFacility;
    }

    const queryString = new URLSearchParams(queryParams);
    getPatientListApi(queryString)
      .then((res) => {
        setLoading(false);
        setPatientData(res?.data);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [
    page,
    searchQuery,
    Number(getCookie(FacilityID)) || userDetails?.currFacility,
  ]);

  const onContactSearch = (value) => {
    const query = value?.target?.value;
    setSearchQuery(query || null);
    setPage(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPage(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const columns = [
    {
      title: "Patient ID",
      key: "patient_id",
      dataIndex: "patient_id",
      width: 100,
    },
    { title: "Phone Number", key: "mobile", dataIndex: "mobile", width: 120 },
    {
      title: "Full Name",
      width: 380,
      render: (_, record) => (
        <Row>
          <Col span={12}>
            {capitalizeFirstLetter(record?.first_name) +
              " " +
              capitalizeFirstLetter(record?.last_name)}
          </Col>
          <Col span={6}>
            <Tag>
              {record?.age} {record?.gender}
            </Tag>
          </Col>
          <Col span={6}>
            {record?.is_inpatient && (
              <>
                {record?.ipadmission__bed__bed_no && (
                  <Tag color="orange">
                    <Image src={bedIcon} preview={false} />{" "}
                    {record?.ipadmission__bed__bed_no}
                  </Tag>
                )}
              </>
            )}
          </Col>
        </Row>
      ),
    },
  ];
  return (
    <UiDataTable
      showHeader={false}
      shellClassName="ui-table-fill-shell"
      onSearch={(e) => onContactSearch(e)}
      searchPlaceholder="Search for patient"
    >
      <div ref={tableWrapRef}>
        <Table
          className="no-vertical-borders"
          bordered={false}
          columns={columns}
          dataSource={patientData?.data}
          pagination={getTablePagination({
            current: page,
            pageSize: 50,
            total: patientData?.total_count ?? 0,
            onChange: handlePageChange,
          })}
          scroll={tableScroll}
          loading={loading}
        />
      </div>
    </UiDataTable>
  );
};

export default PatientsTable;
